import { useCallback, useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import {
	ModalButtonColor,
	ModalType,
	VehicleTuningCategory,
	WalletType,
} from '~/shared';
import {
	getWheelSettingsTotalPrice,
	isDefaultValues,
} from '../components/WheelSettingsTab';
import { useLayoutSelector } from '../redux';
import { getEffectiveTuningSelection } from '../utils/resolveTuningShopVariant';

const useBuy = () => {
	const ownedWheelSettings = useObservable(
		rage.vehicleTuningShop.getOwnedWheelsSettings(),
	);
	const activeCategory = useLayoutSelector(
		(state) => state.tuning.activeCategory,
	);
	const activeStage = useLayoutSelector((state) => state.tuning.activeStage);
	const activeList = useLayoutSelector((state) => state.tuning.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.tuning.activeListIndex,
	);
	const wheelsSettings = useLayoutSelector(
		(state) => state.tuning.wheelSettings,
	);
	const wheelsSetupPrice = useObservable(
		rage.vehicleTuningShop.getWheelsSetupPrice(),
	);
	const activeVariants = useLayoutSelector(
		(state) => state.tuning.activeVariants,
	);

	const wheelBuyActive = useMemo(
		() =>
			ownedWheelSettings
				? !isDefaultValues(wheelsSettings, ownedWheelSettings)
				: false,
		[wheelsSettings, ownedWheelSettings],
	);

	const handleBuy = useCallback(
		(walletType: WalletType) => {
			if (activeStage === 'list') {
				if (activeListIndex === null) {
					return;
				}

				const item = activeList[activeListIndex];
				const effective = getEffectiveTuningSelection(item, activeVariants);

				const text = `Вы действительно хотите оплатить ${
					walletType === WalletType.Cash ? 'наличными' : 'картой'
				} товар "${effective.installLabel}"`;

				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: text,
						price: effective.price,
						wallet: walletType,
						acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result) => {
						if (result.isAccepted) {
							const selectedWallet =
								(result as { value?: WalletType }).value ?? walletType;
							if (activeCategory === VehicleTuningCategory.KITS) {
								rage.vehicleTuningShop.buy(
									{
										itemsKit: item.id,
									},
									selectedWallet,
								);
							} else {
								rage.vehicleTuningShop.buy(
									{
										items: [effective.id],
									},
									selectedWallet,
								);
							}
						}
					},
				);
			}

			if (activeStage === 'wheel_settings' && wheelBuyActive) {
				const text = `Вы действительно хотите оплатить ${
					walletType === WalletType.Cash ? 'наличными' : 'картой'
				} настройку колёс`;

				rage.modal.showModal(
					{
						type: ModalType.Price,
						title: 'Подтверждение',
						text: text,
						price: ownedWheelSettings
							? getWheelSettingsTotalPrice(
									wheelsSettings,
									wheelsSetupPrice,
									ownedWheelSettings,
							  )
							: 0,
						wallet: walletType,
						acceptButton: { text: 'Оплатить', color: ModalButtonColor.Success },
						cancelButton: {
							text: 'Отменить',
							color: ModalButtonColor.Secondary,
						},
					},
					(result) => {
						if (result.isAccepted) {
							const selectedWallet =
								(result as { value?: WalletType }).value ?? walletType;
							rage.vehicleTuningShop.buy(
								{
									wheels: wheelsSettings,
								},
								selectedWallet,
							);
						}
					},
				);
			}
		},
		[
			activeCategory,
			activeStage,
			activeListIndex,
			activeList,
			activeVariants,
			wheelsSetupPrice,
			wheelsSettings,
			ownedWheelSettings,
			wheelBuyActive,
		],
	);

	return { handleBuy };
};

export { useBuy };
